import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setURLPaths } from "../../app/appInitializeReducer";
import { HTTP_STATUS } from "../../http/constants/http.status";
import MenuButtonsPortal from "../Menu/MenuButtonsPortal";
import BiButton from "../primitives/buttons/BiButton.primitive";
import LoaderContainer from "../shared/loaderContainer/LoaderContainer";
import { getUserName } from "../../util/admin-utils";
import { capitalize, isEmpty, replace, startCase } from "lodash";
import { RouteConstants } from "../../constants/RouteConstants";
import { AppConstants } from "../../constants/AppConstants";
import { useTheme } from "../../context/themeContext";
import { generateToken } from "./Util";

import { listRequests } from "../../http/ticket-service";
import { setToastData } from "../../app/toastReducer";
import TemplateTable from "../shared/tables/TemplateTable";
import { ITableHeader } from "../shared/tables/models/ITableHeader";
import moment from "moment";
import { setRequestDetails } from "../../app/requestReducer";
import { useCheckMobileScreen } from "../../util/customHooks";
import { IconButton } from "../primitives/buttons/IconButton";
import plusIcon from "../../assets/plus.svg";
import { plus } from "../../assets/components/svgs";

/**
 * @parentId patient-request
 * @manager Request Listing
 * @overview User's Past Request List
 * <section>
 *   <p>
 *     This page displays a table listing all the past requests made by the logged-in user. Users can access the "Past Requests" page by clicking on the <b>Request History</b> navigation menu item under the <strong>Data Subject Request</strong> menu group of the portal's left sidebar.
 *     The table provides a clear overview of the requests and supports pagination for easy navigation. The default page size is set to 5 items, with options to change it to 5, 10, or 20 from the dropdown menu in the table footer.
 *     The table is sorted by the timestamp of the request creation in descending order.
 *     Datagrid footer will also display the current page and the total number of pages available based on the chosen page size & requests data. Users can switch to next/previous pages using the <b>Next</b> & <b>Previous</b> buttons enabled based on the content in the data grid footer.
 *   </p>
 *   <br>
 *   <p>
 *     Users also have the ability to create a new request by clicking on the <b>New Request</b> button provided in the portal toolbar.
 *   </p>
 *   <br>
 *   <p>
 *     The table displays details of each request as a row item, with columns representing the following properties:
 *     <br>
 *     <ul>
 *       <li>Status</li>
 *       <li>Type</li>
 *       <li>Requested For</li>
 *       <li>Created on</li>
 *     </ul>
 *   </p>
 * </section>
 * <section>
 *   <h4>Failure Status Codes</h4>
 *   <p>
 *     This section describes the status codes for failures related to the Zendesk User Requests.
 *     <br>
 *     <table>
 *       <tr>
 *         <th>HTTP Status Code</th>
 *         <th>Service Error Code</th>
 *         <th>Error Message</th>
 *       </tr>
 *       <tr>
 *         <td>401</td>
 *         <td>UMS_AUTH_REQUIRED</td>
 *         <td>You must authenticate before you can use user-management-service.</td>
 *       </tr>
 *       <tr>
 *         <td>500</td>
 *         <td>NA</td>
 *         <td>Internal Server Error</td>
 *       </tr>
 *     </table>
 *   </p>
 * </section>
 * <section>
 *   <h4>Dependent System settings, Platform services & Role Permission</h4>
 *   <p>This section describes the list of dependent system settings & platform services required for the functioning of the Zendesk User Requests page.</p>
 *   <h5>System Settings</h5>
 *   <p>Table lists all the dependent system setting(s) defined in configuration service with either global/organization scope.</p>
 *   <br>
 *   <table>
 *     <tr>
 *       <th>Key</th>
 *       <th>Type</th>
 *       <th>Value</th>
 *       <th>Scope</th>
 *     </tr>
 *     <tr>
 *       <td>None</td>
 *       <td>None</td>
 *       <td>None</td>
 *       <td>None</td>
 *     </tr>
 *   </table>
 *   <br>
 *   <h5>Platform Service(s)</h5>
 *   <p>Table lists all the dependent platform service(s) with specific version(s) for Zendesk User Requests.</p>
 *   <br>
 *   <table>
 *     <tr>
 *       <th>Service Name</th>
 *       <th>Version</th>
 *     </tr>
 *     <tr>
 *       <td>Zendesk API</td>
 *       <td>V2</td>
 *     </tr>
 *     <tr>
 *       <td>User Management Service</td>
 *       <td>1.2.2</td>
 *     </tr>
 *   </table>
 *   <br>
 *   <h5>API Role Permission(s)</h5>
 *   <p>Table lists the required API role permissions for the Zendesk User Requests page.</p>
 *   <br>
 *   <table>
 *     <tr>
 *       <th>API URL</th>
 *       <th>API Method</th>
 *       <th>API Permission</th>
 *       <th>Required</th>
 *     </tr>
 *   </table>
 * </section>
 * <section>
 *   <p>Sequence Diagram for Zendesk User Requests List</p>
 * </section>
 */

const PastRequests = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [requestList, setRequestList] = useState<any>("");
  const [size, setSize] = useState(AppConstants.DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState<number>(
    AppConstants.DEFAULT_PAGE
  );
  const [pagingOpts, setPagingOpts] = useState<any>(null);

  const [isLoading, setIsLoading] = useState(true);
  const isMobileScreen = useCheckMobileScreen();

  const TABLE_HEADERS: Array<ITableHeader> = [
    {
      key: "status",
      description: t("T_STATUS"),
      sortEnabled: false,
      width: "15%",
      clickable: false,
      sortingInformation: {
        order: "",
      },
      render: (data: any) => {
        return capitalize(data.status);
      },
    },
    {
      key: "fields",
      description: t("T_TYPE"),
      sortEnabled: false,
      clickable: true,
      width: "25%",
      sortingInformation: {
        order: "",
      },
      render: (data: any) => {
        return t(startCase(replace(data?.fields[2]?.value, /_/g, " "))); //TODO
      },
    },
    {
      key: "fields",
      description: t("T_ZENDESK_REQUESTED_FOR"),
      sortEnabled: false,
      clickable: true,
      width: "30%",
      sortingInformation: {
        order: "",
      },
      render: (data: any) => {
        return capitalize(data?.fields[0]?.value); //TODO
      },
    },
    {
      key: "created_at",
      description: t("T_CREATED_ON"),
      sortEnabled: false,
      clickable: true,
      width: "30%",
      sortingInformation: {
        order: "",
      },
      render: (row: any) => {
        return moment(row?.created_at).format(
          isMobileScreen
            ? AppConstants.MOBILE_DATA_FORMAT
            : AppConstants.DEFAULT_DATE_FORMAT
        );
      },
    },
  ];

  const [tableHeaders, setTableHeaders] = useState(TABLE_HEADERS);

  useEffect(() => {
    const init = async () => {
      setBreadCrump();
      await fetchRequests();
    };
    init();
  }, []);

  // useEffect(() => {
  //   const init = async () => {
  //     await fetchRequests();
  //   };
  //   if (!isEmpty(userProfile)) {
  //     init();
  //   }
  // }, [userProfile]);

  const setBreadCrump = async () => {
    dispatch(
      setURLPaths([
        {
          key: RouteConstants.ROUTE_GDPR_LIST,
          label: `${t("T_PATIENT_REQUESTS")} > ${t("T_REQUEST_HISTORY")}`,
        },
      ])
    );
  };

  // const createZendeskUser = async () => {
  //   const payload = {
  //     user: {
  //       name: getUserName(userProfile),
  //       email: userProfile?.email,
  //       skip_verify_email: true,
  //       verified: true,
  //     },
  //   };

  //   let response = await createUser(payload);
  //   return response;
  // };
  const fetchRequests = async (
    page: number = AppConstants.DEFAULT_PAGE,
    pageSize: number = size
  ): Promise<void> => {
    try {
      const listResponse = await listRequests(page, pageSize, "");
      const { data, status } = listResponse;
      const { requests = [], previous_page = null, next_page = null } = data;
      let success = HTTP_STATUS.isSuccess(status);
      if (success && requests.length) {
        setRequestList(requests);
        setPagingOpts({
          page: page,
          size: requests.length,
          first: null,
          previous: previous_page,
          next: next_page,
        });
        setIsLoading(false);
        return;
      } else if (success && !requests.length) {
        setRequestList(`${t("T_NO_DATA_AVAILABLE_MSG")}`);
        setPagingOpts({
          page: page,
          size: 0,
          first: null,
          previous: previous_page,
          next: next_page,
        });
        setIsLoading(false);
        return;
      } else {
        setIsLoading(false);
        setRequestList(capitalize(t("T_ERROR_SOMETHING_WRONG")));
      }
    } catch (error: any) {
      const listResponse = error.response;
      handleErrorMessage("T_ERROR_SOMETHING_WRONG");
      setIsLoading(false);
      setRequestList(capitalize(t("T_ERROR_SOMETHING_WRONG")));

      // if (listResponse.status === HTTP_STATUS.HTTP_UNAUTHORIZED) {
      //   await handleUnauthorized(listResponse);
      // } else if (listResponse.status === HTTP_STATUS.HTTP_FORBIDEN) {
      //   await handleForbidden();
      // }
    }
  };

  // const handleUnauthorized = async (response: any): Promise<void> => {
  //   const userResponse = await createZendeskUser();
  //   const userSuccess: boolean = HTTP_STATUS.isSuccess(userResponse.status);

  //   if (userSuccess) {
  //     setRequestList([]);
  //     setIsLoading(false);
  //   } else {
  //     handleErrorMessage("T_ERROR_SOMETHING_WRONG");
  //   }
  // };

  // const handleForbidden = async (): Promise<void> => {
  //   const getUserResponse = await getUser(generateToken(userProfile?.mail));
  //   const getUserSuccess: boolean = HTTP_STATUS.isSuccess(
  //     getUserResponse.status
  //   );

  //   if (getUserSuccess) {
  //     const userId = getUserResponse.data?.user?.id;
  //     if (userId) {
  //       let patchRes = await updateUserVerification(userId);
  //       if (patchRes) {
  //         await fetchRequests();
  //       }
  //     }
  //   } else {
  //     handleErrorMessage("T_ERROR_SOMETHING_WRONG");
  //   }
  // };

  // const updateUserVerification = async (userId: number): Promise<boolean> => {
  //   const patchUserVerification = await updateUser(
  //     {
  //       user: {
  //         verified: true,
  //       },
  //     },
  //     userId
  //   );
  //   return HTTP_STATUS.isSuccess(patchUserVerification?.status);
  // };

  const handleErrorMessage = (messageKey: string): void => {
    dispatch(
      setToastData({
        isToastActive: true,
        toastMessage: t(messageKey),
        toastType: AppConstants.TOAST_TYPE_ERROR,
      })
    );
  };

  const fetchPaginatedResults = async (
    searchText: string,
    page: number,
    pageSize: number
  ) => {
    let updatedPage = size !== pageSize ? AppConstants.DEFAULT_PAGE : page;
    setSize(pageSize);
    setCurrentPage(updatedPage);
    setIsLoading(true);
    await fetchRequests(updatedPage, pageSize);
  };

  const navigateDetails = (data: any) => {
    dispatch(setRequestDetails(data));
    navigate(RouteConstants.ROUTE_GDPR_VIEW.replace(":id", data?.id));
  };

  return (
    <LoaderContainer isLoading={isLoading}>
      <MenuButtonsPortal>
        {isMobileScreen ? (
          <IconButton
            className={
              "!m-0 h-10 w-10 border border-solid border-primary-disabled text-primary"
            }
            onClick={() => {
              navigate(RouteConstants.ROUTE_GDPR_CREATE);
            }}
          >
            {plus}
          </IconButton>
        ) : (
          <BiButton
            className="flex flex-row bg-primary text-white"
            type="button"
            onClick={() => {
              navigate(RouteConstants.ROUTE_GDPR_CREATE);
            }}
          >
            <img
              src={plusIcon}
              className="mr-2 h-4 w-4 self-center text-center"
            ></img>
            {t("T_NEW_PATIENT_REQUEST_CREATE_REQUEST")}
          </BiButton>
        )}
      </MenuButtonsPortal>
      <div
        className="flex max-h-screen w-full flex-col justify-start overflow-y-scroll  bg-gray-100 px-2 pt-4"
        style={useTheme().theme.bgColorStyleForLoggedInPage}
      >
        <TemplateTable
          tableData={requestList}
          size={size.toString()}
          setSize={setSize}
          currentPage={currentPage.toString()}
          templateSelector={navigateDetails}
          onRefreshTableData={fetchPaginatedResults}
          tableHeaders={tableHeaders}
          setCurrentPage={setCurrentPage}
          setTableHeaders={setTableHeaders}
          containerClass="mt-0"
          searchPlaceholder={t("T_SEARCH_PLACEHOLDER_HINT") || ""}
          pagingData={pagingOpts}
          theme={useTheme().theme}
          isSearchable={false}
          isPaginated
        />
      </div>
    </LoaderContainer>
  );
};

export default PastRequests;
